import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { CdkDragDrop, transferArrayItem, copyArrayItem } from '@angular/cdk/drag-drop';
import { 
  SituViewWebApiClient, AccessoryBundle, GetAccessoryBundleRequest, GetAccessoryBundleResponse,
  UpdateAccessoryBundleRequest, UpdateAccessoryBundleResponse, DeleteAccessoryBundleRequest,
  DeleteAccessoryBundleResponse, CreateAccessoryBundleItem, ListIfcFilesByHomeIdRequest, 
  ListIfcFilesByHomeIdResponse, AccessoryBundleItem, HomeIfcFile
} from 'situview-webapi-client';
import { AuthService, BreadcrumbService, StateService, ConfirmDialogService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { RenameDialogComponent } from 'src/app/components/rename-dialog/rename-dialog.component';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FormControl } from '@angular/forms';

interface ExtendedHomeIfcFile extends HomeIfcFile {
  homeName?: string;
  code?: string;
}

@Component({
  selector: 'accessory-bundle-edit',
  templateUrl: './accessory-bundle-edit.component.html',
  styleUrls: ['./accessory-bundle-edit.component.scss']
})
export class AccessoryBundleEditComponent extends SituBaseComponent implements OnInit {
  id: string = '';
  data: AccessoryBundle | undefined;
  selectedItems: AccessoryBundleItem[] = [];
  availableIfcFiles: ExtendedHomeIfcFile[] = [];
  filteredIfcFiles: ExtendedHomeIfcFile[] = [];
  
  searchControl = new FormControl();
  isModified: boolean = false;
  originalItems: AccessoryBundleItem[] = [];

  private _loading = false;
  public get loading(): boolean { return this._loading; }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private situViewApiClient: SituViewWebApiClient,
    private dialog: MatDialog,
    private dialogService: ConfirmDialogService
  ) {
    super(authService, stateService);
    this.id = this.route.snapshot.params['id'];
  }

  protected onSelectedCompanyChange(): void {
    this.load();
  }

  ngOnInit(): void {
    super.ngOnInit();
    
    this._subscriptions.push(
      this.searchControl.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged()
        )
        .subscribe(value => {
          this.filterIfcFiles(value);
        })
    );
  }

  drop(event: CdkDragDrop<any[]>): void {
    if (event.container.id === 'selectedContainer' && event.previousContainer.id === 'selectedContainer') {
      // Reordering within selected items
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      this.updateSortOrder();
      this.checkIfModified();
    } else if (event.container.id === 'selectedContainer') {
      // Adding new item from available list
      const ifcFile: ExtendedHomeIfcFile = event.previousContainer.data[event.previousIndex];
      const newItem = this.convertToBundleItem(ifcFile);
      
      copyArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      
      event.container.data[event.currentIndex] = newItem;
      this.updateSortOrder();
      this.checkIfModified();
    }
  }

  onRemoveItem(index: number): void {
    this.selectedItems.splice(index, 1);
    this.updateSortOrder();
    this.checkIfModified();
  }

  isItemSelected(ifcFileId: string): boolean {
    return this.selectedItems.some(item => item.homeIfcFileId === ifcFileId);
  }

  save(): void {
    if (!this.data?.id || !this.canSave()) return;

    this.loading = true;
    
    const request = new UpdateAccessoryBundleRequest();
    request.id = this.data.id;
    request.companyId = this.selectedCompany?.id || '';
    request.name = this.data.name;
    request.buildingInstructions = this.data.buildingInstructions;
    request.items = this.selectedItems.map(item => {
      const bundleItem = new CreateAccessoryBundleItem();
      bundleItem.homeIfcFileId = item.homeIfcFileId;
      bundleItem.code = item.code;
      bundleItem.sortOrder = item.sortOrder;
      return bundleItem;
    });

    this.situViewApiClient.updateAccessoryBundle(request)
      .then((response: UpdateAccessoryBundleResponse) => {
        if (response.success) {
          this.snackBar.open('Accessory bundle updated successfully!', 'OK', { duration: 2000 });
          this.load();
        } else {
          this.snackBar.open('Error updating accessory bundle!', 'OK', { duration: 2000 });
        }
        this.loading = false;
      })
      .catch((err: any) => {
        this.loading = false;
        this.snackBar.open('Error updating accessory bundle!', 'OK', { duration: 2000 });
      });
  }

  canSave(): boolean {
    return this.isModified && !!this.data?.name;
  }

  cancel(): void {
    this.load();
    this.isModified = false;
  }

  delete() {
    this.dialogService.open({
      title: 'Delete Accessory Bundle',
      message: `Are you sure you want to delete "${this.data?.name}"? This action cannot be undone.`,
      cancelText: 'Cancel',
      confirmText: 'Delete',
      actionIcon: 'delete',
      actionColour: 'warn'
    } as ConfirmDialogData);

    this._subscriptions.push(this.dialogService.confirmed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.performDelete();
        }
      }));
  }

  private performDelete() {
    if (!this.data?.id || !this.selectedCompany?.id) return;

    this.loading = true;
    
    let request = new DeleteAccessoryBundleRequest();
    request.id = this.data.id;
    request.companyId = this.selectedCompany?.id || '';

    this.situViewApiClient.deleteAccessoryBundle(request)
      .then((response: DeleteAccessoryBundleResponse) => {
        this.loading = false;
        if (response.success) {
          this.snackBar.open('Accessory bundle deleted successfully!', 'OK', { duration: 2000 });
          this.router.navigate(['/admin/accessory-bundle']);
        } else {
          this.snackBar.open('Error deleting accessory bundle!', 'OK', { duration: 2000 });
        }
      })
      .catch((err: any) => {
        this.loading = false;
        this.snackBar.open('Error deleting accessory bundle!', 'OK', { duration: 2000 });
      });
  }

  private load(): void {
    this.loading = true;

    let request = new GetAccessoryBundleRequest();
    request.id = this.id;
    request.companyId = this.selectedCompany?.id;

    this.situViewApiClient.getAccessoryBundle(request)
      .then((response: GetAccessoryBundleResponse) => {
        if (response.success && response.accessoryBundle) {
          this.breadcrumbService.setParam('accessoryBundleName', response.accessoryBundle.name);
          this.data = response.accessoryBundle;
          this.selectedItems = response.accessoryBundle.items || [];
          this.originalItems = JSON.parse(JSON.stringify(this.selectedItems));
          this.loadIfcFiles();
        } else {
          this.snackBar.open('Could not fetch the accessory bundle. Refresh Page.', 'OK', { duration: 2000 });
        }
        this.loading = false;
      })
      .catch((err: any) => {
        this.snackBar.open('Could not find accessory bundle.', 'OK', { duration: 2000 });
        this.loading = false;
      });
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

  private loadIfcFiles(): void {
    if (!this.selectedCompany?.id) return;

    this.loading = true;
    this.availableIfcFiles = [];
    
    const request = new ListIfcFilesByHomeIdRequest();
    request.companyId = this.selectedCompany.id;
    request.isMaster = false; // We only want option files
    
    this.situViewApiClient.listIfcFilesByHomeId(request)
      .then((response: ListIfcFilesByHomeIdResponse) => {
        if (response.success && response.ifcFiles) {
          this.availableIfcFiles = response.ifcFiles.map((file: HomeIfcFile) => {
            // Extract code and home from callerReferenceKey - format: builder\home\code
            const parts = file.callerReferenceKey?.split('\\') || [];
            const builder = parts.length > 0 ? parts[0] : '';
            const homeName = parts.length > 1 ? parts[1] : '';
            const code = parts.length > 2 ? parts[2] : '';
            
            
            return {
              ...file,
              homeName: homeName,
              code: code
            } as ExtendedHomeIfcFile;
          });
          
          // Filter to only show files marked as accessories
          this.availableIfcFiles = this.availableIfcFiles.filter(f => f.isAccessory === true);
          
          this.filteredIfcFiles = [...this.availableIfcFiles];
          
          // Update selected items with proper home code from IFC files
          this.selectedItems = this.selectedItems.map(item => {
            const matchingFile = this.availableIfcFiles.find(f => f.id === item.homeIfcFileId);
            if (matchingFile) {
              item.code = matchingFile.code || '';
              item.homeName = matchingFile.homeName;
            }
            return item;
          });
          
          // Update original items to match
          this.originalItems = JSON.parse(JSON.stringify(this.selectedItems));
        } else {
          this.availableIfcFiles = [];
          this.filteredIfcFiles = [];
        }
        this.loading = false;
      })
      .catch((error) => {
        this.loading = false;
        this.snackBar.open('Error loading IFC files!', 'OK', { duration: 2000 });
        this.availableIfcFiles = [];
        this.filteredIfcFiles = [];
      });
  }

  private filterIfcFiles(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredIfcFiles = [...this.availableIfcFiles];
    } else {
      const term = searchTerm.toLowerCase();
      this.filteredIfcFiles = this.availableIfcFiles.filter(file => 
        file.name?.toLowerCase().includes(term) ||
        file.code?.toLowerCase().includes(term)
      );
    }
  }

  private convertToBundleItem(ifcFile: ExtendedHomeIfcFile): AccessoryBundleItem {
    return {
      homeIfcFileId: ifcFile.id!,
      code: ifcFile.code || '',
      sortOrder: this.selectedItems.length,
      homeName: ifcFile.homeName,
      homeIfcFileName: ifcFile.name
    } as AccessoryBundleItem;
  }

  private updateSortOrder(): void {
    this.selectedItems.forEach((item, index) => {
      item.sortOrder = index;
    });
  }

  checkIfModified(): void {
    const itemsChanged = JSON.stringify(this.selectedItems) !== JSON.stringify(this.originalItems);
    this.isModified = itemsChanged;
  }
}